import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowUp, ExternalLink, MapPin, Clock, DollarSign, Zap, Activity, Wind, Waves, Brain, Sun, Heart, Volume2, Sparkles, Check, X as XIcon, Send, Loader2 } from 'lucide-react';
import { setProgressForSlug } from '../../pages/GuidesHubPage';
import RelatedGuides from './RelatedGuides';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ARTICLE_SLUG = 'the-ammortal-chamber-a-deep-dive-into-the-future-of-wellness';

// ── NAV ITEMS ──
const NAV_ITEMS = [
  { id: 'overview', label: 'Overview' },
  { id: 'modalities', label: 'Modalities' },
  { id: 'science', label: 'Science' },
  { id: 'who', label: "Who's Using It" },
  { id: 'session', label: 'Session' },
  { id: 'stack', label: 'Recovery Stack' },
  { id: 'compare', label: 'Compare' },
  { id: 'cost', label: 'Cost' },
  { id: 'locations', label: 'Locations' },
  { id: 'faq', label: 'FAQ' },
];

// ── MODALITY DATA ──
const MODALITIES = [
  {
    id: 'pbm',
    name: 'Full-Body Photobiomodulation',
    shortName: 'Red + NIR Light',
    icon: Sun,
    color: '#ef4444',
    specs: '660nm (red) + 850nm (near-infrared) @ 100mW/cm\u00B2',
    description: "Photobiomodulation (PBM) isn't new \u2014 it's been studied extensively for cellular regeneration, inflammation reduction, and tissue repair. The Chamber delivers full-body coverage at clinical-grade power density.",
    detail: 'Red light (660nm) penetrates skin tissue and supports collagen production and wound healing. Near-infrared (850nm) penetrates deeper into muscle and joint tissue, supporting cellular energy production and recovery.',
    research: 'Over 700 peer-reviewed studies support PBM for cellular energy production, inflammation reduction, and tissue repair. A 2014 meta-analysis in Lasers in Medical Science found significant benefits for musculoskeletal conditions.',
    soloTime: '20 min',
    soloCost: '$50-100',
    articleLink: '/blogs/article/how-to-select-the-correct-size-red-light-panel',
    articleText: 'How to select the correct size red light panel',
  },
  {
    id: 'pemf',
    name: 'PEMF & PEF Therapy',
    shortName: 'PEMF',
    icon: Zap,
    color: '#8b5cf6',
    specs: '0.5 Hz \u2013 50 MHz frequency range (analog bioharmonic)',
    description: 'Pulsed Electromagnetic Field (PEMF) therapy works at the cellular level, promoting repair and enhancing the effectiveness of other modalities.',
    detail: "PEMF has been used in healthcare for decades. It's FDA-cleared for bone healing (non-union fractures) and has extensive research supporting pain reduction and enhanced recovery. The Ammortal system also uses pulsed electric fields (PEF) to enhance cellular membrane permeability.",
    research: 'PEMF is FDA-cleared for bone healing. A 2020 systematic review found evidence supporting PEMF for osteoarthritis pain and function improvement.',
    soloTime: '30 min',
    soloCost: '$40-80',
  },
  {
    id: 'hydrogen',
    name: 'Molecular Hydrogen Inhalation',
    shortName: 'H\u2082 Therapy',
    icon: Wind,
    color: '#06b6d4',
    specs: '600mL/min delivered via nasal cannula',
    description: 'Molecular hydrogen (H\u2082) is a selective antioxidant that crosses the blood-brain barrier. Unlike broad-spectrum antioxidants, H\u2082 specifically targets harmful reactive oxygen species.',
    detail: 'H\u2082 selectively targets harmful reactive oxygen species without disrupting beneficial oxidative signaling \u2014 a critical distinction from vitamin C or NAC.',
    research: 'Over 1,500 published studies on molecular hydrogen for inflammation, oxidative stress, and cognitive function. A 2020 review in Medical Gas Research highlighted its therapeutic potential for exercise recovery.',
    soloTime: '30 min',
    soloCost: '$30-60',
    articleLink: '/blogs/article/where-can-i-buy-hydrogen-water',
    articleText: 'Where to buy hydrogen water',
  },
  {
    id: 'vat',
    name: 'Full-Body Vibroacoustic Therapy',
    shortName: 'Vibroacoustic',
    icon: Volume2,
    color: '#f59e0b',
    specs: 'Low-frequency sound vibrations synchronized with music',
    description: 'Vibroacoustic therapy (VAT) combines low-frequency sound vibrations with music to create a full-body resonance experience.',
    detail: 'The Chamber features custom-composed multi-act musical journeys synchronized with vibration patterns. Users often describe feeling like they\'re "floating on a cloud."',
    research: 'Studies have shown VAT can reduce anxiety, pain perception, and muscle tension. A 2016 study found significant improvements in fibromyalgia symptoms.',
    soloTime: '45 min',
    soloCost: '$60-120',
  },
  {
    id: 'breathwork',
    name: 'Guided Breathwork',
    shortName: 'Breathwork',
    icon: Activity,
    color: '#22c55e',
    specs: 'Journey-specific protocols synced with other modalities',
    description: "Purpose-built breathwork sequences designed to maximize the therapeutic window \u2014 not generic meditation apps.",
    detail: 'Options include protocols for energy activation, deep relaxation, and cognitive enhancement. The breathwork guidance adapts to the session type you select.',
    research: 'Breathwork protocols have well-established evidence for HRV improvement, stress reduction, and autonomic nervous system regulation.',
    soloTime: '15 min',
    soloCost: '$20-40',
  },
  {
    id: 'zerog',
    name: 'Zero-Gravity Positioning',
    shortName: 'Zero-G',
    icon: Sparkles,
    color: '#a78bfa',
    specs: "Based on NASA's astronaut research for high-G launches",
    description: "The Chamber's bed geometry creates optimal spine, hip, and knee positioning, reducing pressure points for maximum therapeutic absorption.",
    detail: 'The design accommodates users from 4\'8" to 6\'8" with maximum comfort. This positioning allows the body to fully relax and receive all modalities simultaneously.',
    research: "NASA's zero-gravity positioning research shows significant reduction in spinal compression and improved blood flow distribution.",
    soloTime: 'N/A',
    soloCost: 'N/A',
  },
  {
    id: 'meditation',
    name: 'Meditation Integration',
    shortName: 'Meditation',
    icon: Brain,
    color: '#ec4899',
    specs: 'Intention-setting and integration guidance',
    description: 'Structured meditation protocols guide you through intention-setting at the start and integration at the conclusion of each session.',
    detail: 'The meditation component helps frame the session mentally, enhancing the mind-body connection with the physical therapies.',
    soloTime: '15 min',
    soloCost: '$15-30',
  },
  {
    id: 'soundscape',
    name: 'Custom Soundscapes',
    shortName: 'Soundscapes',
    icon: Waves,
    color: '#14b8a6',
    specs: 'Multi-act musical journeys composed for the Chamber',
    description: 'Purpose-composed musical journeys that synchronize with the vibroacoustic therapy and breathwork protocols for a cohesive experience.',
    detail: 'Multiple session types available with different sonic landscapes designed for energy, relaxation, or cognitive focus.',
    soloTime: '30 min',
    soloCost: '$0-20',
  },
  {
    id: 'voice',
    name: 'Voice Guidance',
    shortName: 'Voice Guide',
    icon: Heart,
    color: '#f43f5e',
    specs: 'User-selectable guide voice options',
    description: 'Personalized voice guidance throughout the session, with multiple voice options to match your preference.',
    soloTime: 'N/A',
    soloCost: 'N/A',
  },
  {
    id: 'air',
    name: 'Oxygen & Ozone Purification',
    shortName: 'Air Quality',
    icon: Wind,
    color: '#38bdf8',
    specs: 'Light air purification for the session space',
    description: 'Clean, purified air environment ensures optimal breathing conditions during hydrogen inhalation and breathwork protocols.',
    soloTime: 'N/A',
    soloCost: 'N/A',
  },
];

// ── LOCATIONS ──
const LOCATIONS = [
  { name: 'Padel United', city: 'New Jersey', duration: '22 min', price: '$36-$60', type: 'Sports Facility', url: 'https://ammortal.com/pages/locations' },
  { name: 'Proper Hotel', city: 'Santa Monica, CA', duration: '25 min', price: '$125', type: 'Luxury Hotel', url: 'https://ammortal.com/pages/locations' },
  { name: 'Centner Wellness', city: 'Miami, FL', duration: '45 min', price: '$325', type: 'Wellness Center', url: 'https://ammortal.com/pages/locations' },
];

// ── FAQ DATA ──
const FAQS = [
  { q: 'What should I expect to feel after my first session?', a: 'Most users report feeling "calm yet energized" \u2014 what Ammortal calls "caffeinated calm." Common feedback includes deep relaxation, mental clarity, reduced tension, and a sense of lightness. Effects can last several hours to days.' },
  { q: 'How often should I use the Ammortal Chamber?', a: 'For optimal recovery and performance: 3-4 sessions per week. For nervous system maintenance: 1-2 sessions per week. Even occasional use provides benefits.' },
  { q: 'Is the Ammortal Chamber safe?', a: 'The Chamber uses non-invasive modalities with established safety profiles. Contraindications include: pregnancy, pacemakers or implanted electronic devices, photosensitivity, and seizure disorders. Consult your physician if you have concerns.' },
  { q: 'Will I feel claustrophobic?', a: 'The Chamber was specifically designed to avoid claustrophobia. Users can see in all directions (left, right, up, down), and there\'s an instant-off push button for immediate exit. Thousands of users report feeling comfortable.' },
  { q: 'Can I combine the Chamber with peptide protocols?', a: 'Yes. Many users integrate Chamber sessions with peptide protocols like BPC-157 or the Wolverine Complex. The cellular-level therapies may enhance peptide effectiveness.' },
  { q: 'Is the Ammortal Chamber FDA approved?', a: 'The Chamber is classified as a general wellness device and does not require FDA clearance under the "General Wellness: Policy on Low Risk Devices" guidelines. It is not intended to diagnose, treat, cure, or prevent any disease.' },
];

// ── RELATED LINKS ──
const RELATED = [
  { title: 'The Wolverine Complex: BPC-157 + TB-500 Stack Guide', href: '/blogs/article/the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing' },
  { title: 'How to Select the Correct Size Red Light Panel', href: '/blogs/article/how-to-select-the-correct-size-red-light-panel' },
  { title: 'Where Can I Buy Hydrogen Water?', href: '/blogs/article/where-can-i-buy-hydrogen-water' },
  { title: 'The Ultimate HYROX Training Guide', href: '/blogs/article/the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready' },
  { title: 'The Complete Guide to Peptide Therapy', href: '/blogs/article/the-complete-guide-to-peptide-therapy-for-athletes' },
  { title: 'The Anterior Midcingulate Cortex', href: '/blogs/article/the-anterior-midcingulate-cortex' },
];

// ── RESEARCH TABLE DATA ──
const RESEARCH_TABLE = [
  { modality: 'Photobiomodulation', status: '700+ peer-reviewed studies', key: 'Cellular ATP production, tissue repair' },
  { modality: 'PEMF', status: 'FDA-cleared for bone healing', key: 'Pain management, recovery' },
  { modality: 'Molecular Hydrogen', status: '1,500+ published studies', key: 'Selective antioxidant, neuroprotection' },
  { modality: 'Vibroacoustic', status: 'Emerging research', key: 'Pain reduction, anxiety relief' },
  { modality: 'Breathwork', status: 'Well-established', key: 'HRV improvement, stress reduction' },
];

// ── SESSION PRICING TABLE ──
const PRICING_TABLE = [
  { location: 'Padel United (NJ)', length: '22 min', price: '$36-$60' },
  { location: 'Proper Hotel (Santa Monica)', length: '25 min', price: '$125' },
  { location: 'Centner Wellness (Miami)', length: '45 min', price: '$325' },
  { location: 'Average Clinic', length: '30 min', price: '$115-$165' },
];

// ── SUB-COMPONENTS ──

function ModalityCard({ modality, isOpen, onToggle }) {
  const Icon = modality.icon;
  return (
    <div className="border border-zinc-800 overflow-hidden group hover:border-zinc-700 transition-colors" data-testid={`modality-card-${modality.id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-4 p-5 text-left cursor-pointer">
        <div className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${modality.color}15`, border: `1px solid ${modality.color}30` }}>
          <Icon className="w-5 h-5" style={{ color: modality.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm sm:text-base font-semibold text-white">{modality.name}</h3>
          <p className="text-xs text-zinc-500">{modality.specs}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-zinc-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[#f5a623]' : ''}`} />
      </button>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-5 pb-5 space-y-4">
          <p className="text-sm text-zinc-300 leading-relaxed">{modality.description}</p>
          {modality.detail && <p className="text-sm text-zinc-400 leading-relaxed">{modality.detail}</p>}
          {modality.research && (
            <div className="bg-blue-950/20 border-l-2 border-blue-500/50 p-3">
              <p className="text-xs text-blue-200/80 leading-relaxed"><strong className="text-blue-200">Research:</strong> {modality.research}</p>
            </div>
          )}
          {modality.articleLink && (
            <Link to={modality.articleLink} className="inline-flex items-center gap-1.5 text-xs text-[#f5a623] hover:underline">
              Read: {modality.articleText} <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-zinc-800" data-testid="ammortal-faq-item">
      <button onClick={onToggle} className="w-full py-4 flex items-center justify-between text-left group cursor-pointer">
        <span className="text-sm sm:text-base font-semibold text-white group-hover:text-[#f5a623] transition-colors pr-4">{faq.q}</span>
        <ChevronDown className={`w-4 h-4 text-[#f5a623] flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      <div className={`transition-all duration-300 ease-in-out overflow-hidden ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`}>
        <p className="text-sm text-zinc-400 leading-relaxed">{faq.a}</p>
      </div>
    </div>
  );
}

function StackingComparison() {
  const [selectedMods, setSelectedMods] = useState([]);
  const stackable = MODALITIES.filter(m => m.soloTime !== 'N/A');

  const toggle = (id) => {
    setSelectedMods(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };

  const selectAll = () => setSelectedMods(stackable.map(m => m.id));
  const clearAll = () => setSelectedMods([]);

  const selected = stackable.filter(m => selectedMods.includes(m.id));

  // Calculate totals for separate sessions
  const totalMinutes = selected.reduce((acc, m) => {
    const mins = parseInt(m.soloTime) || 0;
    return acc + mins;
  }, 0);

  const totalCostLow = selected.reduce((acc, m) => {
    const match = m.soloCost.match(/\$(\d+)/);
    return acc + (match ? parseInt(match[1]) : 0);
  }, 0);

  const totalCostHigh = selected.reduce((acc, m) => {
    const matches = m.soloCost.match(/\$(\d+)(?:-\$?(\d+))?/);
    if (matches && matches[2]) return acc + parseInt(matches[2]);
    if (matches && matches[1]) return acc + parseInt(matches[1]);
    return acc;
  }, 0);

  return (
    <section id="ac-compare" className="mt-20 mb-16 scroll-mt-16" data-testid="stacking-comparison">
      <div className="border border-zinc-800 overflow-hidden">
        <div className="bg-zinc-900 p-5 sm:p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3 mb-1">
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#f5a623] bg-[#f5a623]/10 border border-[#f5a623]/20 px-2 py-0.5">Interactive</span>
            <span className="text-[10px] font-mono tracking-widest uppercase text-zinc-600">Modality Calculator</span>
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl text-white">STACKING COMPARISON</h2>
          <p className="text-xs text-zinc-500 mt-1">Select modalities to compare: doing them separately vs. one Chamber session.</p>
        </div>

        <div className="p-5 sm:p-6">
          {/* Modality selection */}
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">Select Modalities</p>
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-[10px] font-mono text-[#f5a623] hover:underline cursor-pointer" data-testid="compare-select-all">Select All</button>
              <span className="text-zinc-700">|</span>
              <button onClick={clearAll} className="text-[10px] font-mono text-zinc-500 hover:text-zinc-300 cursor-pointer" data-testid="compare-clear">Clear</button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
            {stackable.map(m => {
              const Icon = m.icon;
              const isSelected = selectedMods.includes(m.id);
              return (
                <button key={m.id} onClick={() => toggle(m.id)}
                  className={`flex items-center gap-2 p-3 border text-left transition-all cursor-pointer ${isSelected ? 'border-[#f5a623]/40 bg-[#f5a623]/[0.04]' : 'border-zinc-800 hover:border-zinc-700'}`}
                  data-testid={`compare-mod-${m.id}`}>
                  <div className="w-6 h-6 rounded-sm flex items-center justify-center flex-shrink-0" style={{ backgroundColor: isSelected ? `${m.color}20` : 'transparent' }}>
                    {isSelected ? <Check className="w-3.5 h-3.5 text-[#f5a623]" /> : <Icon className="w-3.5 h-3.5" style={{ color: m.color }} />}
                  </div>
                  <span className={`text-xs ${isSelected ? 'text-white' : 'text-zinc-400'}`}>{m.shortName}</span>
                </button>
              );
            })}
          </div>

          {/* Comparison results */}
          {selected.length > 0 && (
            <div className="space-y-4" data-testid="compare-results">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Separate */}
                <div className="border border-zinc-700 p-5">
                  <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-3">Separate Sessions</p>
                  <div className="space-y-2 mb-4">
                    {selected.map(m => (
                      <div key={m.id} className="flex items-center justify-between text-xs">
                        <span className="text-zinc-400">{m.shortName}</span>
                        <span className="text-zinc-500">{m.soloTime} / {m.soloCost}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-zinc-800 pt-3 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-400">Total Time</span>
                      <span className="text-sm font-semibold text-red-400">{totalMinutes} min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-400">Est. Cost</span>
                      <span className="text-sm font-semibold text-red-400">${totalCostLow}-${totalCostHigh}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-400">Sessions</span>
                      <span className="text-sm font-semibold text-red-400">{selected.length} separate</span>
                    </div>
                  </div>
                </div>

                {/* Chamber */}
                <div className="border border-[#f5a623]/30 bg-[#f5a623]/[0.02] p-5">
                  <p className="text-[10px] font-mono tracking-widest uppercase text-[#f5a623] mb-3">Ammortal Chamber</p>
                  <div className="space-y-2 mb-4">
                    {selected.map(m => {
                      const Icon = m.icon;
                      return (
                        <div key={m.id} className="flex items-center gap-2 text-xs">
                          <Check className="w-3 h-3 text-[#f5a623]" />
                          <span className="text-zinc-300">{m.shortName}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-[#f5a623]/10 pt-3 space-y-1">
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-400">Total Time</span>
                      <span className="text-sm font-semibold text-[#f5a623]">30 min</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-400">Est. Cost</span>
                      <span className="text-sm font-semibold text-[#f5a623]">$100-$165</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-zinc-400">Sessions</span>
                      <span className="text-sm font-semibold text-[#f5a623]">1 integrated</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Savings callout */}
              {selected.length >= 3 && (
                <div className="bg-[#f5a623]/[0.06] border border-[#f5a623]/20 p-4 text-center">
                  <p className="text-sm text-white font-semibold">
                    You save ~{totalMinutes - 30} minutes and ${Math.max(totalCostLow - 165, 0)}-${Math.max(totalCostHigh - 100, 0)} per session
                  </p>
                  <p className="text-xs text-zinc-400 mt-1">Plus the synergistic benefit of simultaneous delivery</p>
                </div>
              )}
            </div>
          )}

          {selected.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-zinc-500">Select modalities above to see the comparison</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function LeadCaptureForm() {
  const [form, setForm] = useState({ email: '', first_name: '', city: '' });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/leads/ammortal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, interests: ['ammortal_chamber', 'session_booking'] }),
      });
      if (!res.ok) throw new Error('Failed to submit');
      setStatus('success');
    } catch (err) {
      setErrorMsg('Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="border border-[#f5a623]/30 bg-[#f5a623]/[0.04] p-6 sm:p-8 text-center" data-testid="lead-form-success">
        <div className="w-12 h-12 mx-auto mb-4 bg-[#f5a623]/10 border border-[#f5a623]/30 rounded-full flex items-center justify-center">
          <Check className="w-6 h-6 text-[#f5a623]" />
        </div>
        <h3 className="font-heading text-xl text-white mb-2">YOU'RE ON THE LIST</h3>
        <p className="text-sm text-zinc-400">We'll notify you when an Ammortal Chamber becomes available near {form.city || 'you'}. Keep an eye on your inbox.</p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 overflow-hidden" data-testid="lead-capture-form">
      <div className="bg-zinc-900 p-5 sm:p-6 border-b border-zinc-800">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-[#f5a623] bg-[#f5a623]/10 border border-[#f5a623]/20 px-2 py-0.5">Notify Me</span>
        </div>
        <h3 className="font-heading text-xl sm:text-2xl text-white">GET NOTIFIED WHEN A CHAMBER IS NEAR YOU</h3>
        <p className="text-xs text-zinc-500 mt-1">Enter your details and we'll let you know when an Ammortal Chamber session becomes available in your area.</p>
      </div>
      <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-1">First Name</label>
            <input
              type="text"
              value={form.first_name}
              onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
              placeholder="Your name"
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#f5a623]/40 focus:outline-none transition-colors"
              data-testid="lead-form-name"
            />
          </div>
          <div>
            <label className="block text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-1">City / Region</label>
            <input
              type="text"
              value={form.city}
              onChange={(e) => setForm(f => ({ ...f, city: e.target.value }))}
              placeholder="e.g. Miami, FL"
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#f5a623]/40 focus:outline-none transition-colors"
              data-testid="lead-form-city"
            />
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-1">Email <span className="text-red-500">*</span></label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
            placeholder="you@email.com"
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#f5a623]/40 focus:outline-none transition-colors"
            data-testid="lead-form-email"
          />
        </div>
        {status === 'error' && <p className="text-xs text-red-400" data-testid="lead-form-error">{errorMsg}</p>}
        <button
          type="submit"
          disabled={status === 'loading' || !form.email}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#f5a623] text-black text-xs font-mono font-bold uppercase tracking-wider hover:bg-[#d4901c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="lead-form-submit"
        >
          {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          {status === 'loading' ? 'Submitting...' : 'Notify Me'}
        </button>
        <p className="text-[10px] text-zinc-600 text-center">No spam. Just Ammortal Chamber availability updates.</p>
      </form>
    </div>
  );
}

// ── MAIN COMPONENT ──
export default function AmmortalChamberArticle() {
  const [activeNav, setActiveNav] = useState('overview');
  const [openMods, setOpenMods] = useState({ pbm: true });
  const [openFaq, setOpenFaq] = useState(0);
  const [progress, setProgress] = useState(0);
  const [showTop, setShowTop] = useState(false);

  const handleScroll = useCallback(() => {
    const h = document.documentElement;
    const pct = Math.min((h.scrollTop / (h.scrollHeight - h.clientHeight)) * 100, 100);
    setProgress(pct);
    setShowTop(h.scrollTop > 500);
    setProgressForSlug(ARTICLE_SLUG, pct);

    const offsets = NAV_ITEMS.map(n => {
      const el = document.getElementById(`ac-${n.id}`);
      return el ? { id: n.id, top: el.getBoundingClientRect().top } : null;
    }).filter(Boolean);
    const current = offsets.filter(o => o.top < 150).pop();
    if (current) setActiveNav(current.id);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollTo = (id) => {
    const el = document.getElementById(`ac-${id}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="ammortal-chamber-article">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 h-[3px] bg-[#f5a623] z-[100] transition-all duration-100" style={{ width: `${progress}%` }} data-testid="reading-progress" />

      {/* Hero */}
      <section className="relative pt-28 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_70%_60%_at_80%_50%,rgba(245,166,35,0.06)_0%,transparent_70%)]" />
        <div className="max-w-3xl mx-auto relative z-10">
          <span className="inline-block text-[10px] font-mono tracking-[0.12em] uppercase text-[#f5a623] border border-[#f5a623]/30 px-3 py-1 mb-6">
            Deep Dive · Recovery Technology · 2026
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white mb-6" data-testid="article-title">
            THE AMMORTAL CHAMBER:<br/><span className="text-[#f5a623]">10 THERAPIES IN 30 MINUTES</span>
          </h1>
          <p className="text-lg text-zinc-300 max-w-xl leading-relaxed mb-8">
            A $159,500 multi-modality wellness pod that stacks PEMF, red light, molecular hydrogen, vibroacoustic therapy, and more into one synchronized session. Complete guide for serious athletes and biohackers.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono text-zinc-400">
            <span>March 2026</span>
            <span>14 min read</span>
            <span>Research citations included</span>
            <span>NinjAthlete</span>
          </div>
        </div>
      </section>

      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-zinc-800/50" data-testid="article-sticky-nav">
        <div className="max-w-3xl mx-auto flex overflow-x-auto scrollbar-hide px-4">
          {NAV_ITEMS.map(n => (
            <button key={n.id} onClick={() => scrollTo(n.id)}
              className={`whitespace-nowrap px-4 py-3 text-[10px] sm:text-[11px] font-mono tracking-wider uppercase border-b-2 transition-colors flex-shrink-0 ${activeNav === n.id ? 'text-[#f5a623] border-[#f5a623]' : 'text-zinc-500 border-transparent hover:text-zinc-300'}`}
              data-testid={`nav-${n.id}`}>
              {n.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-6 py-14 sm:py-20">

        {/* OVERVIEW */}
        <section id="ac-overview" className="mb-16 scroll-mt-16">
          <div className="border-l-2 border-[#f5a623] pl-6 mb-10">
            <p className="text-lg text-zinc-300 leading-relaxed">
              The pursuit of optimal recovery has led athletes and biohackers down countless paths: red light panels, PEMF mats, hydrogen water, breathwork protocols. Each modality has merit. Each requires time, equipment, and often separate sessions.
            </p>
          </div>
          <p className="text-base text-zinc-400 leading-relaxed mb-4">
            What if you could stack them all into a single 30-minute experience? The <strong className="text-white">Ammortal Chamber</strong> integrates 10+ scientifically-validated therapies into one synchronized session.
          </p>
          <p className="text-base text-zinc-400 leading-relaxed mb-8">
            Whether you're pushing through{' '}
            <Link to="/blogs/article/the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready" className="text-[#f5a623] hover:underline">demanding HYROX training cycles</Link>,
            recovering from injury, or pursuing the frontiers of longevity science \u2014 here's everything you need to know.
          </p>

          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
            {[
              { num: '10+', title: 'Therapies', sub: 'Stacked in one session' },
              { num: '3,000+', title: 'Sessions Tracked', sub: 'Internal user-reported data' },
              { num: '95%', title: 'Deep Relaxation', sub: 'User-reported outcome' },
              { num: '30', title: 'Minutes', sub: 'Standard session length' },
            ].map((s, i) => (
              <div key={s.title} className="border border-zinc-800 p-4 group hover:border-[#f5a623]/20 transition-colors" data-testid={`stat-card-${i}`}>
                <p className="text-3xl sm:text-4xl font-heading text-[#f5a623] leading-none mb-2">{s.num}</p>
                <p className="text-xs font-semibold text-white mb-1">{s.title}</p>
                <p className="text-[10px] text-zinc-500 leading-tight">{s.sub}</p>
              </div>
            ))}
          </div>

          <p className="text-sm text-zinc-400 leading-relaxed">
            The core philosophy: <strong className="text-white">stress is cumulative, so recovery should be too.</strong> By layering PEMF, photobiomodulation, vibroacoustic therapy,{' '}
            <Link to="/blogs/article/where-can-i-buy-hydrogen-water" className="text-[#f5a623] hover:underline">molecular hydrogen</Link>, and guided breathwork, the Chamber shifts your nervous system from fight-or-flight to rest-and-repair.
          </p>
        </section>

        {/* MODALITIES */}
        <section id="ac-modalities" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-3">THE 10+ MODALITIES</h2>
          <p className="text-sm text-zinc-400 mb-6">Each therapy compounds the others. Click to expand details and research.</p>
          <div className="space-y-2">
            {MODALITIES.map(m => (
              <ModalityCard
                key={m.id}
                modality={m}
                isOpen={!!openMods[m.id]}
                onToggle={() => setOpenMods(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
              />
            ))}
          </div>
        </section>

        {/* SCIENCE */}
        <section id="ac-science" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">WHAT THE SCIENCE SAYS</h2>
          <p className="text-base text-zinc-400 leading-relaxed mb-4">
            While each individual modality has substantial research backing, the combined effect of simultaneous delivery is still being studied. Ammortal positions the Chamber as a general wellness device.
          </p>
          <p className="text-base text-zinc-400 leading-relaxed mb-6">
            Data from over 3,000 sessions shows consistent user-reported outcomes:
          </p>

          {/* Outcome bars */}
          <div className="space-y-3 mb-8">
            {[
              { label: 'Deep Full-Body Relaxation', pct: 95, color: '#f5a623' },
              { label: 'Energetic Reset ("Caffeinated Calm")', pct: 90, color: '#f59e0b' },
              { label: 'Increased Mental Clarity & Focus', pct: 85, color: '#d97706' },
            ].map((bar, i) => (
              <div key={bar.label} data-testid={`outcome-bar-${i}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-zinc-300">{bar.label}</span>
                  <span className="text-xs font-mono font-bold" style={{ color: bar.color }}>{bar.pct}%</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${bar.pct}%`, backgroundColor: bar.color }} />
                </div>
              </div>
            ))}
            <p className="text-[10px] text-zinc-600 mt-1">Based on internal surveys of 3,000+ sessions</p>
          </div>

          {/* Research table */}
          <div className="border border-zinc-800 overflow-hidden" data-testid="research-table">
            <div className="grid grid-cols-3 bg-zinc-900 border-b border-zinc-800">
              <div className="p-3 text-[10px] font-mono uppercase tracking-wider text-[#f5a623]">Modality</div>
              <div className="p-3 text-[10px] font-mono uppercase tracking-wider text-[#f5a623]">Research Status</div>
              <div className="p-3 text-[10px] font-mono uppercase tracking-wider text-[#f5a623]">Key Studies</div>
            </div>
            {RESEARCH_TABLE.map((row, i) => (
              <div key={row.key} className={`grid grid-cols-3 border-b border-zinc-800/50 ${i % 2 === 0 ? '' : 'bg-zinc-900/30'}`}>
                <div className="p-3 text-xs text-white font-semibold">{row.modality}</div>
                <div className="p-3 text-xs text-zinc-400">{row.status}</div>
                <div className="p-3 text-xs text-zinc-500">{row.key}</div>
              </div>
            ))}
          </div>
        </section>

        {/* WHO'S USING IT */}
        <section id="ac-who" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">WHO'S USING THE AMMORTAL CHAMBER?</h2>
          <p className="text-base text-zinc-400 leading-relaxed mb-6">
            When professional athletes with access to unlimited recovery resources choose a specific modality, it's worth paying attention.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {[
              { label: 'NFL Teams', detail: 'Including the Denver Broncos', icon: '🏈' },
              { label: 'MLB Organizations', detail: 'Arizona Diamondbacks — Bobby Miller credits it for recovery', icon: '⚾' },
              { label: 'Biohacking Leaders', detail: 'Dave Asprey featured it at his Biohacking Conference', icon: '🧬' },
              { label: "Shark Tank's Daymond John", detail: 'Entrepreneur and investor', icon: '💼' },
              { label: 'Elite Wellness Clinics', detail: 'Nationwide adoption', icon: '🏥' },
              { label: "Goop's Science Team", detail: 'Featured editorial review', icon: '📰' },
            ].map((item, i) => (
              <div key={item.label} className="flex items-start gap-3 p-4 border border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`user-card-${i}`}>
                <span className="text-xl flex-shrink-0">{item.icon}</span>
                <div>
                  <p className="text-sm font-semibold text-white">{item.label}</p>
                  <p className="text-xs text-zinc-500">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-zinc-400 leading-relaxed">
            For athletes training for{' '}
            <Link to="/blogs/article/the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready" className="text-[#f5a623] hover:underline">HYROX races</Link>,
            the time-efficiency of 10 modalities in 30 minutes is a significant advantage over separate recovery protocols.
          </p>
        </section>

        {/* SESSION EXPERIENCE */}
        <section id="ac-session" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">WHAT TO EXPECT IN A SESSION</h2>
          <div className="space-y-3 mb-6">
            {[
              { step: '01', title: 'Pre-Session Customization', desc: 'Select your desired outcome (energy, relaxation, focus), guide voice, meditation style, and music preference.' },
              { step: '02', title: 'Entry', desc: 'The lid raises automatically for easy entry. Lie down on the zero-gravity bed.' },
              { step: '03', title: 'Session Start', desc: 'The lid lowers with full visibility. Red and NIR light activates, vibrations begin, breathwork guidance starts.' },
              { step: '04', title: 'Immersion', desc: 'Music, vibration, light, and hydrogen delivery synchronize into a choreographed experience.' },
              { step: '05', title: 'Integration', desc: 'Voice-guided integration and gentle return to alertness.' },
              { step: '06', title: 'Post-Session', desc: 'Most users report "caffeinated calm" \u2014 alert but deeply peaceful.' },
            ].map((s, i) => (
              <div key={s.title} className="flex items-start gap-4 p-4 border border-zinc-800 hover:border-[#f5a623]/20 transition-colors" data-testid={`session-step-${i}`}>
                <span className="text-2xl font-heading text-[#f5a623] flex-shrink-0 w-8">{s.step}</span>
                <div>
                  <p className="text-sm font-semibold text-white mb-1">{s.title}</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-amber-950/20 border border-amber-700/30 p-4">
            <p className="text-xs text-amber-200/80"><strong className="text-amber-200">User Tip:</strong> First-time users often experience deeper effects than expected. Start with a 25-30 minute session rather than jumping to 60 minutes.</p>
          </div>
        </section>

        {/* RECOVERY STACK */}
        <section id="ac-stack" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">HOW IT FITS YOUR RECOVERY STACK</h2>
          <p className="text-base text-zinc-400 leading-relaxed mb-6">
            The Chamber can serve as the anchor point for a comprehensive recovery approach.
          </p>
          <div className="space-y-4">
            {[
              {
                title: 'For Athletes',
                text: <>Following a demanding program like <Link to="/blogs/article/the-ultimate-guide-to-hyrox-training-plans-and-workouts-get-race-ready" className="text-[#f5a623] hover:underline">HYROX workouts</Link>? The Chamber serves as your primary recovery 2-3x/week, with <Link to="/blogs/article/the-ultimate-guide-to-high-protein-meal-prep" className="text-[#f5a623] hover:underline">high-protein nutrition</Link> filling gaps.</>
              },
              {
                title: 'For Biohackers',
                text: <>Pairs with <Link to="/blogs/article/the-wolverine-complex-the-ultimate-peptide-stack-for-rapid-recovery-and-healing" className="text-[#f5a623] hover:underline">Wolverine Complex (BPC-157 + TB-500)</Link>, <Link to="/blogs/article/mots-c-vs-metformin-longevity-comparison" className="text-[#f5a623] hover:underline">MOTS-c protocols</Link>, <Link to="/blogs/article/dihexa-peptide-brain-repair-what-the-science-actually-says" className="text-[#f5a623] hover:underline">Dihexa</Link> for cognitive enhancement, and <Link to="/blogs/article/the-power-of-methylation-health-and-wellness-from-within" className="text-[#f5a623] hover:underline">methylation support</Link>.</>
              },
              {
                title: 'For Mindset Training',
                text: <>Research on the <Link to="/blogs/article/the-anterior-midcingulate-cortex" className="text-[#f5a623] hover:underline">anterior midcingulate cortex</Link> shows challenge and recovery cycles build psychological resilience. The Chamber provides the recovery half.</>
              },
            ].map((item, i) => (
              <div key={item.title} className="border border-zinc-800 p-5 hover:border-[#f5a623]/20 transition-colors">
                <h3 className="text-sm font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* STACKING COMPARISON TOOL */}
        <StackingComparison />

        {/* COST */}
        <section id="ac-cost" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">COST BREAKDOWN</h2>

          {/* Session pricing table */}
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-3">Session Pricing by Location</p>
          <div className="border border-zinc-800 overflow-hidden mb-8" data-testid="pricing-table">
            <div className="grid grid-cols-3 bg-zinc-900 border-b border-zinc-800">
              <div className="p-3 text-[10px] font-mono uppercase tracking-wider text-[#f5a623]">Location</div>
              <div className="p-3 text-[10px] font-mono uppercase tracking-wider text-[#f5a623]">Length</div>
              <div className="p-3 text-[10px] font-mono uppercase tracking-wider text-[#f5a623]">Price</div>
            </div>
            {PRICING_TABLE.map((row, i) => (
              <div key={row.location} className={`grid grid-cols-3 border-b border-zinc-800/50 ${i % 2 === 0 ? '' : 'bg-zinc-900/30'}`}>
                <div className="p-3 text-xs text-white">{row.location}</div>
                <div className="p-3 text-xs text-zinc-400">{row.length}</div>
                <div className="p-3 text-xs text-zinc-400">{row.price}</div>
              </div>
            ))}
          </div>

          {/* Purchase pricing */}
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-3">Purchase</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            {[
              { label: 'Chamber Price', value: '$159,500' },
              { label: 'Deposit (10%)', value: '$15,950' },
              { label: 'Financing', value: '~$3,000/mo' },
              { label: 'Warranty', value: '2 years' },
              { label: 'Includes', value: 'White-glove install' },
              { label: 'Ongoing Costs', value: 'Minimal' },
            ].map((item, i) => (
              <div key={item.label} className="border border-zinc-800 p-4">
                <p className="text-[10px] text-zinc-500 mb-1">{item.label}</p>
                <p className="text-sm font-semibold text-white">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Worth it section */}
          <div className="space-y-3">
            {[
              { who: 'For individuals', text: 'At $100-150/session, comparable to premium recovery. The time savings \u2014 10 therapies in 30 min vs 2+ hours \u2014 makes the math work.' },
              { who: 'For clinics', text: '6-8 sessions/day at $100+ generates substantial revenue with minimal staff overhead. A market differentiator.' },
              { who: 'For home buyers', text: 'Combined cost of high-end red light, PEMF, hydrogen, and vibroacoustic systems \u2014 integrated synergistically.' },
            ].map((item, i) => (
              <div key={item.text} className="flex gap-3 p-3 bg-zinc-900/30 border border-zinc-800/50">
                <span className="text-xs font-semibold text-[#f5a623] flex-shrink-0 w-28">{item.who}</span>
                <p className="text-xs text-zinc-400 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* LOCATIONS */}
        <section id="ac-locations" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">FIND A CHAMBER</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {LOCATIONS.map((loc, i) => (
              <a key={loc.name} href={loc.url} target="_blank" rel="noopener noreferrer"
                className="border border-zinc-800 p-5 hover:border-[#f5a623]/30 transition-all group block"
                data-testid={`location-card-${i}`}>
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-[#f5a623]" />
                  <span className="text-[10px] font-mono text-zinc-500 uppercase">{loc.type}</span>
                </div>
                <h3 className="text-sm font-semibold text-white group-hover:text-[#f5a623] transition-colors mb-1">{loc.name}</h3>
                <p className="text-xs text-zinc-500 mb-3">{loc.city}</p>
                <div className="flex items-center gap-4 text-xs text-zinc-400">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{loc.duration}</span>
                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{loc.price}</span>
                </div>
              </a>
            ))}
          </div>
          <div className="bg-zinc-900 border border-zinc-800 p-5 text-center">
            <p className="text-sm text-zinc-300 mb-3">More locations available nationwide</p>
            <a href="https://ammortal.com/pages/locations" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#f5a623] text-black text-xs font-mono font-bold uppercase tracking-wider hover:bg-[#d4901c] transition-colors"
              data-testid="find-locations-cta">
              Find All Locations <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </section>

        {/* Lead Capture Form */}
        <div className="mb-16">
          <LeadCaptureForm />
        </div>

        {/* FAQ */}
        <section id="ac-faq" className="mb-16 scroll-mt-16">
          <h2 className="font-heading text-3xl sm:text-4xl text-white mb-6">FREQUENTLY ASKED QUESTIONS</h2>
          <div>
            {FAQS.map((f, i) => (
              <FAQItem key={f.q} faq={f} isOpen={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </section>

        {/* Related Links */}
        <div className="bg-zinc-900/30 border border-zinc-800/50 p-6 mb-16">
          <p className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 mb-4">Continue Your Research</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {RELATED.map((r, i) => (
              <Link key={r.title} to={r.href} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#f5a623] transition-colors py-1">
                <span className="text-[#f5a623] text-xs">&#8594;</span> {r.title}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="border border-[#f5a623]/30 bg-[#f5a623]/[0.03] p-6 text-center mb-16">
          <h3 className="font-heading text-2xl text-white mb-3">READY TO EXPERIENCE IT?</h3>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="https://ammortal.com/pages/locations" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#f5a623] text-black text-xs font-mono font-bold uppercase tracking-wider hover:bg-[#d4901c] transition-colors"
              data-testid="cta-find-chamber">
              Find a Chamber <MapPin className="w-3.5 h-3.5" />
            </a>
            <a href="https://ammortal.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs font-mono font-bold uppercase tracking-wider hover:border-[#f5a623]/40 hover:text-[#f5a623] transition-colors"
              data-testid="cta-learn-more">
              Learn More <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        <RelatedGuides currentSlug={ARTICLE_SLUG} />

        {/* Disclaimer */}
        <div className="bg-amber-950/20 border border-amber-700/30 p-4 text-xs text-amber-200/70 leading-relaxed">
          <strong className="text-amber-200">Disclaimer:</strong> This content is for informational purposes only. The Ammortal Chamber is a general wellness device and is not intended to diagnose, treat, cure, or prevent any disease. Always consult with a healthcare provider before beginning any new wellness protocol. NinjAthlete is not affiliated with Ammortal Inc.
        </div>
      </main>

      {/* Scroll to top */}
      {showTop && (
        <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-6 w-10 h-10 bg-zinc-900 border border-zinc-700 rounded-full flex items-center justify-center text-white hover:bg-[#f5a623] hover:text-black transition-colors z-50"
          data-testid="scroll-to-top">
          <ArrowUp className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
